import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { EyeOff, Link2, SmilePlus, Tag, Trash2, Undo2 } from 'lucide-react';
import type { CommentView, ReactionView, TopicView } from '@retro/shared';
import { useRetroStore } from '../../lib/retroStore.js';
import { Avatar, IconButton } from '../ui.js';
import { TopicChipList, TopicPicker } from './TopicChips.js';
import { EmojiGroupList, EmojiQuickRow } from './EmojiGroupList.js';
import { REACTIONS } from './emojiGroups.js';

/**
 * "Ada, Grace and You reacted with 👍" — built from the names the server sent,
 * with the viewer written as "You" and placed first. Filtering by name is a
 * deliberate simplification: two participants sharing a display name collapse
 * into one "You", which is harmless here.
 */
function reactionLabel(reaction: ReactionView, myName: string): string {
  const others = reaction.names.filter((name) => name !== myName);
  const people = reaction.mine ? ['You', ...others] : others;
  const list =
    people.length <= 1
      ? (people[0] ?? 'Nobody')
      : `${people.slice(0, -1).join(', ')} and ${people[people.length - 1]}`;
  return `${list} reacted with ${reaction.emoji}`;
}

/**
 * The palette popover and the pill row, shared by comment cards and group
 * headers so "groups react like comments" is one implementation, not two that
 * look alike today.
 *
 * The palette opens toward the body of the thing it belongs to, so it always
 * overlays content that is already on screen. Which way that is depends on
 * where the bar sits, so the caller picks: the card's bar is a footer with the
 * card's text above it (`'above'`), while the group's bar sits above the
 * group's cards (`'below'`). Opening upward unconditionally put the group's
 * palette past the group's top edge, where the column scrollport clipped it.
 *
 * Position resolves against the nearest positioned ancestor — the card's
 * footer and the group's reaction row both carry `relative` for exactly this
 * reason. Horizontal placement is deliberately row-relative (`right-2`), not
 * trigger-relative: the group's trigger sits at the left edge of the row, and
 * right-aligning to it would drop the palette off the left side of the
 * column, which clips horizontally too.
 */
export function ReactionBar({
  reactions,
  canReact,
  myName,
  onToggle,
  placement = 'above',
}: {
  reactions: ReactionView[];
  canReact: boolean;
  myName: string;
  onToggle: (emoji: string) => void;
  /** Where the palette opens relative to this row; the default is the card case. */
  placement?: 'above' | 'below';
}) {
  const [showReactions, setShowReactions] = useState(false);
  const [showAllReactions, setShowAllReactions] = useState(false);
  const barRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const mine = new Set(reactions.filter((reaction) => reaction.mine).map((reaction) => reaction.emoji));

  const closePalette = () => {
    setShowReactions(false);
    setShowAllReactions(false);
  };

  useEffect(() => {
    if (!showReactions) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePalette();
        triggerRef.current?.focus();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (barRef.current && !barRef.current.contains(event.target as Node)) {
        closePalette();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [showReactions]);

  const renderChoice = (emoji: string) => {
    const held = mine.has(emoji);
    return (
      <button
        key={emoji}
        type="button"
        aria-label={
          held ? `You reacted with ${emoji} · click to remove yours` : `Add your ${emoji} reaction`
        }
        className={clsx(
          'rounded-md px-1.5 py-1 font-emoji text-base hover:bg-black/5',
          held && 'bg-brand-100 text-brand-700',
        )}
        onClick={() => {
          onToggle(emoji);
          closePalette();
          triggerRef.current?.focus();
        }}
      >
        {emoji}
      </button>
    );
  };

  return (
    <span ref={barRef} className="contents">
      {reactions.length > 0 ? (
        <span className="flex flex-wrap gap-1">
          {reactions.map((reaction) => {
            const label = `${reactionLabel(reaction, myName)}${
              canReact ? (reaction.mine ? ' · click to remove yours' : ' · click to add yours') : ''
            }`;
            return (
              <button
                key={reaction.emoji}
                type="button"
                disabled={!canReact}
                onClick={() => onToggle(reaction.emoji)}
                className={clsx(
                  'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs transition',
                  reaction.mine ? 'bg-brand-100 text-brand-700' : 'bg-black/5 text-ink/70',
                  canReact ? 'hover:bg-brand-100' : 'cursor-default opacity-50',
                )}
                title={label}
                aria-label={label}
              >
                <span className="font-emoji">{reaction.emoji}</span>
                <span className="tabular-nums">{reaction.count}</span>
              </button>
            );
          })}
        </span>
      ) : null}

      {canReact ? (
        <IconButton
          ref={triggerRef}
          label="Add a reaction"
          onClick={() => (showReactions ? closePalette() : setShowReactions(true))}
        >
          <SmilePlus size={16} aria-hidden />
        </IconButton>
      ) : null}

      {showReactions ? (
        // A named group, not a menu: menu promises arrow-key navigation between
        // menuitems, which this does not implement — the buttons are simply
        // tabbable, and the role must not advertise more than that.
        <div
          role="group"
          aria-label="Choose a reaction"
          className={clsx(
            // Never wider than the row: a card inside a group is narrower than the
            // palette, and anything past the row's left edge is clipped by the column.
            'absolute right-2 z-10 max-w-[calc(100%-1rem)] rounded-xl bg-white p-1 shadow-panel',
            placement === 'above' ? 'bottom-full mb-1' : 'top-full mt-1',
            showAllReactions ? 'max-h-60 w-72 overflow-y-auto' : 'flex flex-wrap gap-0.5',
          )}
        >
          {showAllReactions ? (
            <EmojiGroupList renderChoice={renderChoice} />
          ) : (
            <EmojiQuickRow
              choices={REACTIONS}
              expandLabel="Show all reactions"
              renderChoice={renderChoice}
              onExpand={() => setShowAllReactions(true)}
            />
          )}
        </div>
      ) : null}
    </span>
  );
}

export function CommentAuthor({ comment }: { comment: CommentView }) {
  if (comment.isAnonymous) {
    return (
      <span className="inline-flex items-center gap-[7px]">
        <span className="anon-av" aria-hidden>
          <EyeOff size={14} />
        </span>
        <span>{comment.isMine ? 'You · anonymous' : 'Anonymous'}</span>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-[7px]">
      <Avatar emoji={comment.author?.avatar ?? ''} name={comment.author?.name ?? 'Unknown'} size="sm" />
      <span>{comment.isMine ? 'You' : (comment.author?.name ?? 'Unknown')}</span>
    </span>
  );
}

/**
 * A revealed comment card.
 *
 * Used from the Group phase onwards; the interactive affordances (reactions,
 * topics, grouping, delete) are switched off individually so the same card can
 * render in a read-only report.
 */
export function CommentCard({
  comment,
  topics,
  canReact,
  canEditTopics,
  canUngroup,
  showAuthor = true,
  dimmed,
  highlightActive,
  myName,
  onHighlightAuthor,
  onGroupWith,
  dragHandleProps,
  isDragging,
  className,
}: {
  comment: CommentView;
  topics: TopicView[];
  canReact?: boolean;
  canEditTopics?: boolean;
  canUngroup?: boolean;
  showAuthor?: boolean;
  dimmed?: boolean;
  highlightActive?: boolean;
  /** The viewer's display name, used to render their own reaction as "You". */
  myName: string;
  onHighlightAuthor?: () => void;
  /**
   * Pointer-free alternative to dragging. Drag-and-drop cannot be the only way
   * to group (WCAG 2.5.7), and it is fiddly on a phone besides.
   */
  onGroupWith?: () => void;
  dragHandleProps?: Record<string, unknown>;
  isDragging?: boolean;
  className?: string;
}) {
  const send = useRetroStore((state) => state.send);
  const [showTopics, setShowTopics] = useState(false);

  return (
    <article
      className={clsx(
        'note group/card relative transition',
        dimmed && 'opacity-35',
        isDragging && 'rotate-1 shadow-panel',
        dragHandleProps && 'cursor-grab active:cursor-grabbing',
        className,
      )}
      {...dragHandleProps}
    >
      {showAuthor ? (
        <div className="note-a">
          {onHighlightAuthor && !comment.isAnonymous ? (
            <button
              type="button"
              onClick={onHighlightAuthor}
              title={
                highlightActive
                  ? 'Show everyone’s comments again'
                  : `Show only ${comment.author?.name}'s comments`
              }
              aria-pressed={highlightActive}
              className="rounded-full transition hover:bg-black/5"
            >
              <CommentAuthor comment={comment} />
            </button>
          ) : (
            <CommentAuthor comment={comment} />
          )}
        </div>
      ) : null}

      <p className="note-t">{comment.text}</p>

      {comment.gifUrl ? (
        <img src={comment.gifUrl} alt="" className="mt-2 max-h-40 w-full rounded-lg object-cover" />
      ) : null}

      {comment.topicIds.length > 0 ? (
        <div className="mt-2">
          <TopicChipList topicIds={comment.topicIds} topics={topics} />
        </div>
      ) : null}

      <div className="note-f relative">
        <ReactionBar
          reactions={comment.reactions}
          canReact={Boolean(canReact)}
          myName={myName}
          onToggle={(emoji) =>
            void send('comment:toggleReaction', { commentId: comment.id, emoji })
          }
        />

        <span className="ml-auto flex flex-wrap items-center gap-1.5 opacity-0 transition group-hover/card:opacity-100 focus-within:opacity-100 max-lg:opacity-100">
          {onGroupWith ? (
            // Grouping is the primary action of this step, and drag-and-drop
            // must not be the only way to do it (WCAG 2.5.7).
            <button
              type="button"
              onClick={onGroupWith}
              className="chip"
              aria-label="Group with another comment"
            >
              <Link2 size={13} aria-hidden /> Group with…
            </button>
          ) : null}
          {canEditTopics ? (
            <button
              type="button"
              className="chip"
              onClick={() => setShowTopics(true)}
              aria-label="Add topics"
            >
              <Tag size={13} aria-hidden />
            </button>
          ) : null}
          {canUngroup ? (
            <button
              type="button"
              className="chip"
              onClick={() => void send('comment:ungroup', { commentId: comment.id })}
              aria-label="Remove from group"
            >
              <Undo2 size={13} aria-hidden /> Ungroup
            </button>
          ) : null}
          {comment.isMine ? (
            <button
              type="button"
              className="chip"
              onClick={() => void send('comment:delete', { commentId: comment.id })}
              aria-label="Delete comment"
            >
              <Trash2 size={13} aria-hidden />
            </button>
          ) : null}
        </span>
      </div>

      <TopicPicker
        commentId={comment.id}
        topicIds={comment.topicIds}
        topics={topics}
        open={showTopics}
        onClose={() => setShowTopics(false)}
      />
    </article>
  );
}
